import { useMemo } from "react";
import { Text as RNText } from "react-native";
import Markdown, {
	type ASTNode,
	AstRenderer,
	renderRules as defaultRenderRules,
	type RenderRules,
	styles as defaultStyles,
} from "react-native-markdown-renderer";
import { Text, type TextProps } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { inlineMarkdownIt } from "./inline-markdown";

export type InlineMarkdownTextProps = Omit<TextProps<never>, "children"> & {
	text?: string | null;
};

const buildInlineRules = (appFont: string): RenderRules => ({
	paragraph: (node: ASTNode, children) => (
		<RNText key={node.key}>{children}</RNText>
	),
	strong: (node: ASTNode, children) => (
		<RNText
			key={node.key}
			style={{ fontFamily: `${appFont}-bold`, fontWeight: "700" }}
		>
			{children}
		</RNText>
	),
	em: (node: ASTNode, children, parent) => {
		const hasStrongAncestor = parent.some((p) => p.type === "strong");
		return (
			<RNText
				key={node.key}
				style={{
					fontFamily: `${appFont}-${hasStrongAncestor ? "bolditalic" : "regularitalic"}`,
					fontStyle: "italic",
				}}
			>
				{children}
			</RNText>
		);
	},
	ins: (node: ASTNode, children) => (
		<RNText key={node.key} style={{ textDecorationLine: "underline" }}>
			{children}
		</RNText>
	),
	code_inline: (node: ASTNode) => (
		<RNText key={node.key}>{node.content}</RNText>
	),
	unknown: (node: ASTNode, children) => (
		<RNText key={node.key}>{children}</RNText>
	),
});

const InlineMarkdownText = ({
	text,
	...textProps
}: InlineMarkdownTextProps) => {
	const theme = useAppTheme();
	const appFont = theme.typography.fonts.regular.fontFamily.replace(
		/-regular$/,
		"",
	);

	const renderer = useMemo(() => {
		const astRenderer = new AstRenderer(
			{ ...defaultRenderRules, ...buildInlineRules(appFont) },
			// blank the default `text` style so spans inherit the outer variant's metrics
			{ ...defaultStyles, text: {} },
		);
		return (nodes: ASTNode[]) => (
			<>
				{nodes.flatMap((node, index) =>
					index === 0
						? [astRenderer.renderNode(node, [])]
						: ["\n\n", astRenderer.renderNode(node, [])],
				)}
			</>
		);
	}, [appFont]);

	return (
		<Text {...textProps}>
			<Markdown markdownit={inlineMarkdownIt} renderer={renderer}>
				{text ?? ""}
			</Markdown>
		</Text>
	);
};

export default InlineMarkdownText;
